import posthog from 'posthog-js';

// Detect if we're in CI environment
const isCI = () => {
	return !!process.env.CI;
};

// No-op functions for CI environment
const noOpPostHog = {
	capture: () => undefined,
	isFeatureEnabled: () => false,
	getFeatureFlag: () => undefined,
	get_distinct_id: () => 'ci-test-id',
	get_session_id: () => 'ci-session-id',
	setPersonProperties: () => undefined,
	setPersonPropertiesForFlags: () => undefined,
	unsetPersonPropertiesForFlags: () => undefined,
	group: () => undefined,
	register: () => undefined,
	register_for_session: () => undefined,
	debug: () => undefined,
	onFeatureFlags: (callback: (...args: unknown[]) => void) => callback(),
	reloadFeatureFlags: () => undefined,
	init: () => undefined,
	isNoOp: () => true,
};

// Type-safe wrapper that matches PostHog interface subset we use
export interface PostHogWrapper {
	capture: (event: string, properties?: Record<string, unknown>) => void;
	isFeatureEnabled: (flag: string) => boolean | undefined;
	getFeatureFlag: (
		flag: string,
		options?: { send_event?: boolean },
	) => string | boolean | undefined;
	get_distinct_id: () => string;
	get_session_id: () => string;
	setPersonProperties: (properties: Record<string, unknown>) => void;
	setPersonPropertiesForFlags: (
		properties: Record<string, unknown>,
		reloadFeatureFlags?: boolean,
	) => void;
	unsetPersonPropertiesForFlags: (propertyNames: string[], reloadFeatureFlags?: boolean) => void;
	group: (groupType: string, groupKey: string, properties?: Record<string, unknown>) => void;
	register: (properties: Record<string, unknown>) => void;
	register_for_session: (properties: Record<string, unknown>) => void;
	debug: () => void;
	onFeatureFlags: (callback: (...args: unknown[]) => void) => void;
	reloadFeatureFlags: () => void;
	init: (apiKey: string, options?: Record<string, unknown>) => void;
	isNoOp?: () => boolean;
}

// Create wrapper that normalizes PostHog behavior
const createPostHogWrapper = (): PostHogWrapper => {
	if (isCI()) {
		return noOpPostHog;
	}

	// Wrap real PostHog to ensure consistent return types
	return {
		capture: posthog.capture.bind(posthog),
		isFeatureEnabled: posthog.isFeatureEnabled.bind(posthog),
		getFeatureFlag: posthog.getFeatureFlag.bind(posthog),
		get_distinct_id: posthog.get_distinct_id.bind(posthog),
		get_session_id: posthog.get_session_id.bind(posthog),
		setPersonProperties: posthog.setPersonProperties.bind(posthog),
		setPersonPropertiesForFlags: posthog.setPersonPropertiesForFlags.bind(posthog),
		// Lives on the featureFlags extension; the top-level class only exposes set/reset, and
		// reset would also wipe flag properties stored by setPersonProperties at lead submit.
		unsetPersonPropertiesForFlags: (propertyNames: string[], reloadFeatureFlags?: boolean) =>
			posthog.featureFlags?.unsetPersonPropertiesForFlags(propertyNames, reloadFeatureFlags),
		group: posthog.group.bind(posthog),
		register: posthog.register.bind(posthog),
		register_for_session: posthog.register_for_session.bind(posthog),
		debug: posthog.debug.bind(posthog),
		onFeatureFlags: posthog.onFeatureFlags.bind(posthog),
		reloadFeatureFlags: posthog.reloadFeatureFlags.bind(posthog),
		init: posthog.init.bind(posthog),
	};
};

// Export PostHog instance
const posthogClient: PostHogWrapper = createPostHogWrapper();

export default posthogClient;
